/* ==========================================================================
   Charliee - persistent mobile bottom stack.

   Keeps the cart pill and delivery meter in step with the bag.

   The theme adds to cart from several places - the quick-commerce product
   card, the sticky add-to-cart, the cart page steppers, and any app block -
   and they do not share one event. Rather than subscribing to each, this
   watches the network: any write to /cart/add|change|update|clear triggers a
   re-read of /cart.js. Both fetch and XMLHttpRequest are wrapped, since older
   app scripts still use XHR. The wrappers pass everything through untouched
   and only observe, so nothing else in the theme changes behaviour.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-cmb]');
  if (!root) return;

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  var threshold = parseInt(root.getAttribute('data-cmb-threshold'), 10) || 0;
  var onCart = root.getAttribute('data-cmb-on-cart') === 'true';
  var meterOn = root.getAttribute('data-cmb-meter') === 'true';

  var copy = {
    lockedTitle: root.getAttribute('data-cmb-locked-title') || 'Unlock free delivery',
    lockedSub: root.getAttribute('data-cmb-locked-sub') || 'Shop for [amount] more',
    doneTitle: root.getAttribute('data-cmb-done-title') || 'Free delivery unlocked',
    doneSub: root.getAttribute('data-cmb-done-sub') || 'Applied at checkout'
  };

  var pills = root.querySelector('[data-cmb-pills]');
  var ship = root.querySelector('[data-cmb-ship]');
  var shipTitle = root.querySelector('[data-cmb-ship-title]');
  var shipSub = root.querySelector('[data-cmb-ship-sub]');
  var shipFill = root.querySelector('[data-cmb-ship-fill]');
  var thumb = root.querySelector('[data-cmb-thumb]');
  var count = root.querySelector('[data-cmb-count]');

  /* ---------- height, published for the body padding and the sticky ATC ---------- */
  function publishHeight() {
    var h = root.offsetHeight || 0;
    document.documentElement.style.setProperty('--cmb-height', h + 'px');
  }

  publishHeight();
  window.addEventListener('resize', publishHeight);
  window.addEventListener('orientationchange', publishHeight);

  if (window.ResizeObserver) {
    new ResizeObserver(publishHeight).observe(root);
  }

  /* ---------- money ----------
     Cart JSON is in paise. Whole rupees print without decimals, because a
     shortfall of "135.00" reads as heavier than it is on a pill this small. */
  function money(paise) {
    var v = Math.abs(paise) / 100;
    var frac = paise % 100 === 0 ? 0 : 2;
    try {
      return '\u20B9' + v.toLocaleString('en-IN', {
        minimumFractionDigits: frac,
        maximumFractionDigits: frac
      });
    } catch (e) {
      return '\u20B9' + v.toFixed(frac);
    }
  }

  function thumbUrl(url) {
    if (!url) return '';
    return url.replace(/(\.[a-z]+)(\?|$)/i, '_96x96$1$2');
  }

  /* ---------- paint ---------- */
  function paint(cart) {
    if (!cart) return;

    var n = cart.item_count || 0;
    var show = n > 0 && !onCart && meterOn;

    if (pills) pills.hidden = !show;

    if (count) count.textContent = n + (n === 1 ? ' item' : ' items');

    if (thumb) {
      var src = thumbUrl(cart.items && cart.items[0] && cart.items[0].image);
      var img = thumb.querySelector('img');
      if (src) {
        if (!img) {
          img = document.createElement('img');
          img.width = 96;
          img.height = 96;
          img.alt = '';
          img.loading = 'lazy';
          thumb.appendChild(img);
        }
        if (img.getAttribute('src') !== src) img.setAttribute('src', src);
      } else if (img) {
        img.remove();
      }
    }

    if (!ship || threshold <= 0) return;

    var paid = cart.total_price || 0;
    var left = threshold - paid;
    var done = left <= 0;
    var filled = Math.max(0, Math.min(100, Math.round((paid * 100) / threshold)));

    ship.classList.toggle('is-done', done);
    if (shipFill) shipFill.style.width = filled + '%';
    if (shipTitle) shipTitle.textContent = done ? copy.doneTitle : copy.lockedTitle;
    if (shipSub) {
      shipSub.textContent = done
        ? copy.doneSub
        : copy.lockedSub.replace('[amount]', money(left));
    }
  }

  /* ---------- read ----------
     Coalesced: several writes can land in one interaction (a stepper held
     down), and each would otherwise queue its own request. */
  var pending = null;

  function refresh() {
    clearTimeout(pending);
    pending = setTimeout(function () {
      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart.js'
        : '/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(paint)
        .catch(function () { /* leave the last good paint in place */ });
    }, 90);
  }

  /* ---------- watch cart writes ---------- */
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function () {
      var out = nativeFetch.apply(this, arguments);
      try {
        var a = arguments[0];
        var url = typeof a === 'string' ? a : (a && a.url) || '';
        if (CART_WRITE.test(url)) out.then(refresh, function () {});
      } catch (e) { /* never let the watcher break a real request */ }
      return out;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      this.__cmbWatch = CART_WRITE.test(String(url));
    } catch (e) {
      this.__cmbWatch = false;
    }
    return nativeOpen.apply(this, arguments);
  };

  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__cmbWatch) this.addEventListener('load', refresh);
    return nativeSend.apply(this, arguments);
  };

  /* Dawn's own pub/sub, when it is present, plus a manual hook. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    try { subscribe(PUB_SUB_EVENTS.cartUpdate, refresh); } catch (e) {}
  }
  document.addEventListener('cart:refresh', refresh);

  /* Back/forward cache returns a stale page with a live cart. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) refresh();
  });
})();
